// 3rd party
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { IconHeart } from 'icons/indexed-icons/IconHeart';

// Constants
// API
import {
  useGetSavedProductsQuery,
  useGetProductsWithSpecifiedCurrenciesQuery,
  useDeleteSavedProductMutation
} from 'api';

// Types
import { ModalSize } from 'types';
import { Product, SavedPart } from 'types/parts';

// Components
import { Badge, Modal, Typography } from 'components';
import { skipToken } from '@reduxjs/toolkit/query';
import SavedProductTable from '../SavedProductTable/SavedProductTable';
import theme from 'themes';
import { useHeader } from 'components/Header/HeaderContext';
  import { useLanguage } from 'providers';
import { getProductDescription } from 'common/helpers/v1/part';

const ImageWishlist = styled.div`
  cursor: pointer;
  font-size: 15px;
  color: ${(props) => props.theme.colors.darkGrey};
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding-left: 2rem;
`;

const WishList = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const TextMobile = styled.div`
  @media (min-width: 541px) {
    display: none;
  }
`;

const ImageMobile = styled.div`
  @media (min-width: 541px) {
    display: none;
  }
`;

const ImageLargeScreen = styled.div`
  @media (max-width: 540px) {
    display: none;
  }
`;

const SavedProductList = (): ReactElement => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { t } = useTranslation(['fpns']);

  const { languageId } = useLanguage();
  const [deleteProduct] = useDeleteSavedProductMutation();
  const { state: headerState, dispatch } = useHeader();

  const {
    data: savedProducts,
    refetch: refreshSavedProductsList,
    isFetching: productsAreLoading,
    isLoading
  } = useGetSavedProductsQuery();
  const { data: products, isLoading: isProcuctLoading } =
    useGetProductsWithSpecifiedCurrenciesQuery(
      savedProducts && savedProducts.length > 0
        ? savedProducts.map((savedProduct) => ({
            id: savedProduct.productId,
            priceUnit: savedProduct.priceUnit
          }))
        : skipToken
    );

  useEffect(() => {
    if (headerState.isShowSavedProduct) {
      setShowModal(true);
    }
  }, [headerState.isShowSavedProduct]);

  const savedParts = useMemo(() => {
    const savedItemsList: Product[] = [];
    products?.forEach((product) => {
      const resp = product.value;
      const item = resp.data;
      const errorItem = resp.data;
      const savedItem = savedProducts?.find(
        (prod) => prod.productId === (item?.id || errorItem.id)
      );

      if (product.value.error) {
        deleteProduct({
          productId: errorItem?.id,
          priceUnit: errorItem?.price_unit || ''
        });
      } else if (item && savedItem) {
        const sp: SavedPart = {
          stock: item.stock,
          description: getProductDescription(item, languageId), //item.description,
          leadTime: item.lead_time,
          price: item.price,
          custPrice: item.cust_price,
          id: item.id,
          quantity: savedItem?.quantity || 1,
          sku: item.sku,
          priceUnit: savedItem.priceUnit || '',
          isPurchasable: item.is_purchasable
        };
        savedItemsList.push(sp);
      }
    });
    return savedItemsList;
  }, [products, savedProducts]);
  const handleCloseModal = () => {
    setShowModal(false);
    dispatch({ type: 'hideSavedProduct' });
  };
  return (
    <>
      <WishList>
        <ImageWishlist>
          <div
            id="saved-icon"
            data-testid="cart-icon"
            onClick={() => setShowModal(true)}
            style={{ paddingTop: 4 }}
          >
            <ImageLargeScreen>
              <IconHeart strokeColor={theme.colors.lightGrey10} />
            </ImageLargeScreen>
            <ImageMobile>
              <IconHeart strokeColor={theme.colors.primaryBlue5} />
            </ImageMobile>
            {savedParts.length > 0 && <Badge style={{ top: 0 }}>{savedParts.length}</Badge>}
          </div>
        </ImageWishlist>
        <TextMobile>
          <span>{t('favorites')}</span>
        </TextMobile>
      </WishList>

      <Modal
        visible={showModal}
        size={ModalSize.SMALL}
        onClose={() => handleCloseModal()}
        title={
          <ModalHeader>
            {
              <Typography as="h3" mb={0} size="1.125rem" weight="bold">
                {t('saved_product_list')}
              </Typography>
            }
          </ModalHeader>
        }
      >
        <SavedProductTable
          data={(savedParts as SavedPart[]) || []}
          onRefresh={refreshSavedProductsList}
          isDataLoading={isProcuctLoading || isLoading || productsAreLoading}
          handleShowModal={() => handleCloseModal()}
        />
      </Modal>
    </>
  );
};

export default SavedProductList;
